// Zadanie: z boku i kąta naprzeciw policz promień okręgu opisanego R
// Wersje proste: α = 30° (R = a), γ = 90° (R = c/2)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

// LaTeX dla wartości sin() kątów z puli
function sinValueLatex(angleDeg) {
  switch (angleDeg) {
    case 30:  return '\\dfrac{1}{2}';
    case 45:  return '\\dfrac{\\sqrt{2}}{2}';
    case 60:  return '\\dfrac{\\sqrt{3}}{2}';
    case 90:  return '1';
    case 120: return '\\dfrac{\\sqrt{3}}{2}';
    case 135: return '\\dfrac{\\sqrt{2}}{2}';
    case 150: return '\\dfrac{1}{2}';
    default:  return '';
  }
}

// ⬇️ w tym samym pliku, obok helperów:
function formatLatexPreviewMixed(raw) {
  const s = String(raw ?? "").trim();
  // mixed number: "A B/C"
  const m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = m[1], B = m[2], C = m[3];
    return `${A}\\;\\dfrac{${B}}{${C}}`;
  }
  // dziesiętne – pokaż z przecinkiem w LaTeX
  if (/^[+-]?\d+[.,]\d+$/.test(s.replace(/\s+/g, ""))) {
    return s.replace(/\s+/g, "").replace(".", "{,}");
  }
  // resztę (pierwiastki, zwykłe ułamki) formatuje standardowy formatter komponentu
  return formatLatexPreview ? formatLatexPreview(s) : s;
}



// po podstawieniu sinusa do 2·sin(...) jaki mamy „mianownik po uproszczeniu”
function denomAfterSin(angleDeg) {
  switch (angleDeg) {
    case 30:
    case 150: return '1';           // 2·(1/2) = 1
    case 90:  return '2';           // 2·1 = 2
    case 45:
    case 135: return '\\sqrt{2}';   // 2·(√2/2) = √2
    case 60:
    case 120: return '\\sqrt{3}';   // 2·(√3/2) = √3
    default:  return '';
  }
}



// Zracjonalizowana postać: a/√2 -> (a√2)/2 (ew. skraca), a/√3 -> (a√3)/3 (ew. skraca)
function rationalizeLatex(num, denomStr) {
  if (denomStr === '\\sqrt{2}') {
    if (num % 2 === 0) return `${num / 2}\\sqrt{2}`;
    return `\\dfrac{${num}\\sqrt{2}}{2}`;
  }
  if (denomStr === '\\sqrt{3}') {
    if (num % 3 === 0) return `${Math.trunc(num / 3)}\\sqrt{3}`;
    return `\\dfrac{${num}\\sqrt{3}}{3}`;
  }
  // fallback – gdyby pojawił się inny zapis mianownika
  return `\\dfrac{${num}}{${denomStr}}`;
}


// mała pomoc do ułamków
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}
function toSimpleFraction(numer, denom) {
  if (denom < 0) { numer = -numer; denom = -denom; }
  const g = gcd(numer, denom);
  return { numerator: numer / g, denominator: denom / g };
}
function equalFractions(a, b) {
  const A = toSimpleFraction(a.numerator, a.denominator);
  const B = toSimpleFraction(b.numerator, b.denominator);
  return A.numerator === B.numerator && A.denominator === B.denominator;
}

export function generateTask() {
  // wybór kąta: α, β, γ
  const angleChoice = Math.floor(Math.random() * 3);
  let angleLabel, sideLabel;
  if (angleChoice === 0) {
    angleLabel = "\\alpha";
    sideLabel = "a";
  } else if (angleChoice === 1) {
    angleLabel = "\\beta";
    sideLabel = "b";
  } else {
    angleLabel = "\\gamma";
    sideLabel = "c";
  }

  // kąty do losowania – bez 90°
  const angleOptions = [30, 45, 60, 120, 135, 150];
  const angleDeg = angleOptions[Math.floor(Math.random() * angleOptions.length)];

  // bok 3..15
  const sideValue = Math.floor(Math.random() * 13) + 3;

  // oczekiwany wynik R
  let expectedR;
  switch (angleDeg) {
    case 30:
    case 150:
      expectedR = { numerator: sideValue, denominator: 1 };
      break;
    case 45:
    case 135:
      expectedR = { numerator: sideValue, denominator: "\\sqrt{2}" };
      break;
    case 60:
    case 120:
      expectedR = { numerator: sideValue, denominator: "\\sqrt{3}" };
      break;
  }

  const data = {
    type: "FindCircumradiusFromSideAngleTask",
    sideLabel,
    sideValue,
    angleLabel,
    angleDeg,
    expectedR,
  };

  return { type: "FindCircumradiusFromSideAngleTask", data };
}



export function renderTask({ data }) {
  const { sideLabel, sideValue, angleLabel, angleDeg } = data;
  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <div>
        W trójkącie <InlineMath math={'ABC'} /> dany jest bok{" "}
        <InlineMath math={`${sideLabel} = ${sideValue}`} /> oraz kąt{" "}
        <InlineMath math={`${angleLabel} = ${angleDeg}^\\circ`} /> leżący naprzeciw
        tego boku. Oblicz promień okręgu opisanego{" "}
        <InlineMath math={'R'} />.
      </div>
    </div>
  );
}



export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="R"
      formatLatexPreview={formatLatexPreviewMixed} // ⬅️ DODANE
    />
  );
}

function preprocessRawForParsing(raw) {
  if (raw == null) return "";
  let s = String(raw).trim();

  // mixed number: "A B/C"
  const mixed = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (mixed) {
    const A = parseInt(mixed[1], 10);
    const B = parseInt(mixed[2], 10);
    const C = parseInt(mixed[3], 10);
    const improper = A >= 0 ? (A * C + B) : (A * C - B);
    return `${improper}/${C}`;
  }

  // decimal with comma -> dot (dla parsera)
  s = s.replace(/\s+/g, "").replace(",", ".");
  return s;
}

function expectedNumeric({ sideValue, angleDeg }) {
  const denomMap = {
    30: 1, 150: 1,
    90: 2,
    45: Math.SQRT2, 135: Math.SQRT2,
    60: Math.sqrt(3), 120: Math.sqrt(3),
  };
  const denom = denomMap[angleDeg] ?? 1;
  return sideValue / denom;
}


export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (raw == null || String(raw).trim() === "") return false;

  // mixed & decimal -> do parsowania
  const pre = preprocessRawForParsing(raw);

  // spróbuj policzyć wartość liczbową
  const userVal = parseRootFractionInput(pre);
  if (userVal == null || !Number.isFinite(userVal)) return false;

  const expVal = expectedNumeric(data);
  return Math.abs(userVal - expVal) < 1e-6;
}


export function renderAnswer({ data }) {
  const { expectedR } = data;

  const isNumber = (x) => typeof x === "number" && Number.isFinite(x);

  // zamiana a/√2 -> (a√2)/2 -> ewentualnie skróć do (a/2)√2
  //        a/√3 -> (a√3)/3 -> ewentualnie skróć do (a/3)√3
  const rationalizeLatex = (num, denomStr) => {
    if (denomStr === "\\sqrt{2}") {
      if (num % 2 === 0) {
        const k = num / 2;
        return `R = ${k}\\sqrt{2}`;
      }
      return `R = \\dfrac{${num}\\sqrt{2}}{2}`;
    }
    if (denomStr === "\\sqrt{3}") {
      if (num % 3 === 0) {
        const k = Math.trunc(num / 3);
        return `R = ${k}\\sqrt{3}`;
      }
      return `R = \\dfrac{${num}\\sqrt{3}}{3}`;
    }
    // fallback (gdyby pojawił się inny zapis)
    return `R = \\dfrac{${num}}{${denomStr}}`;
  };

  let latex;
  if (isNumber(expectedR.denominator)) {
    // przypadek liczbowy – uprość zwykły ułamek
    const { numerator, denominator } = toSimpleFraction(
      Number(expectedR.numerator),
      Number(expectedR.denominator)
    );
    latex = denominator === 1
      ? `R = ${numerator}`
      : `R = \\dfrac{${numerator}}{${denominator}}`;
  } else {
    // przypadek z pierwiastkiem – racyonalizacja
    latex = rationalizeLatex(Number(expectedR.numerator), String(expectedR.denominator));
  }

  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <InlineMath math={latex} />
    </div>
  );
}


export function renderExplanation({ data }) {
  const { sideLabel, sideValue, angleLabel, angleDeg } = data;

  const sinLatex = sinValueLatex(angleDeg);
  const after = denomAfterSin(angleDeg); // 1, 2, √2 albo √3

  // końcowy wynik (z racjonalizacją, gdy trzeba)
  let finalR;
  if (after === '1') {
    finalR = `${sideValue}`;
  } else if (after === '2') {
    const f = toSimpleFraction(sideValue, 2);
    finalR = f.denominator === 1 ? `${f.numerator}` : `\\dfrac{${f.numerator}}{${f.denominator}}`;
  } else {
    finalR = rationalizeLatex(sideValue, after);
  }

  return (
    <div style={{ textAlign: 'left', marginTop: '8px', lineHeight: 1.7 }}>
      {/* Krok 1: wzór */}
      <div><strong>Krok 1.</strong> Twierdzenie sinusów i wzór na promień:</div>
      <BlockMath math={'\\dfrac{a}{\\sin\\alpha}=\\dfrac{b}{\\sin\\beta}=\\dfrac{c}{\\sin\\gamma}=2R'} />
      <BlockMath math={'\\Rightarrow\\quad R=\\dfrac{\\text{bok}}{2\\sin(\\text{k\\c{a}t})}'} />

        <div><strong>Krok 2.</strong> Podstawiamy dane:</div>
        <BlockMath math={`R = \\dfrac{${sideValue}}{2\\sin ${angleDeg}^\\circ}`} />

        <div><strong>Krok 3.</strong> Wartość sinusa:</div>
        {angleDeg > 90 ? (
        <>
            <BlockMath math={`\\sin ${angleDeg}^\\circ = \\sin(180^\\circ - ${angleDeg}^\\circ)`} />
            <BlockMath math={`\\sin ${angleDeg}^\\circ = \\sin ${180 - angleDeg}^\\circ`} />
            <BlockMath math={`\\sin ${angleDeg}^\\circ = ${sinLatex}`} />
        </>
        ) : (
        <BlockMath math={`\\sin ${angleDeg}^\\circ = ${sinLatex}`} />
        )}

      {/* Krok 4: uproszczenie 2·sin(...) */}
      <div style={{ marginTop: '6px' }}><strong>Krok 4.</strong> Uproszczamy mianownik:</div>
      <BlockMath math={`R=\\dfrac{${sideValue}}{2\\cdot ${sinLatex}}`} />
      <BlockMath
        math={
          after === '1'
            ? `R=${sideValue}`
            : after === '2'
              ? `R=\\dfrac{${sideValue}}{2}`
              : `R=\\dfrac{${sideValue}}{${after}}`
        }
      />

      {/* Krok 5: racjonalizacja (dla √2 lub √3) */}
      {(after === '\\sqrt{2}' || after === '\\sqrt{3}') && (
        <>
          <div style={{ marginTop: '6px' }}><strong>Krok 5.</strong> Usuwamy niewymierność z mianownika:</div>
          <BlockMath
            math={
              after === '\\sqrt{2}'
                ? `\\dfrac{${sideValue}}{\\sqrt{2}}=\\dfrac{${sideValue}\\cdot\\sqrt{2}}{\\sqrt{2}\\cdot\\sqrt{2}}=\\dfrac{${sideValue}\\sqrt{2}}{2}`
                : `\\dfrac{${sideValue}}{\\sqrt{3}}=\\dfrac{${sideValue}\\cdot\\sqrt{3}}{\\sqrt{3}\\cdot\\sqrt{3}}=\\dfrac{${sideValue}\\sqrt{3}}{3}`
            }
          />
          {/* ewentualne skrócenie */}
          <BlockMath math={`R=${finalR}`} />
        </>
      )}

      {/* przypadki bez racjonalizacji */}
      {after !== '\\sqrt{2}' && after !== '\\sqrt{3}' && (
        <div style={{ marginTop: '6px' }}>
          <strong>Odpowiedź:</strong>
          <BlockMath math={`R=${finalR}`} />
        </div>
      )}
    </div>
  );
}

// === PRETTY LATEX z RAW (dla RootFractionInput) ===
function stripOuterParens(s) {
  s = s.trim();
  while (s.startsWith("(") && s.endsWith(")")) {
    // spróbuj zdjąć tylko jeśli nawiasy są zbalansowane
    let depth = 0, ok = true;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === "(") depth++;
      else if (s[i] === ")") depth--;
      if (depth < 0) { ok = false; break; }
    }
    if (ok && depth === 0) s = s.slice(1, -1).trim(); else break;
  }
  return s;
}

// ZAMIENIAMY poprzednie formatRootFracLatex na:
function formatRootFracLatex(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";

  // mixed number: "A B/C" -> pokaż jako A  B/C
  const m = s.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const A = m[1], B = m[2], C = m[3];
    return `${A}\\;\\dfrac{${B}}{${C}}`;
  }

  // dziesiętne -> z przecinkiem
  if (/^[+-]?\d+[.,]\d+$/.test(s.replace(/\s+/g, ""))) {
    return s.replace(/\s+/g, "").replace(".", "{,}");
  }

  // normalizacja reszty (pierwiastki, zwykłe ułamki)
  s = stripOuterParens(s)
    .replace(/\u2212/g, "-")
    .replace(/\s+/g, "")
    .replace(/sqrt\(([^)]+)\)/g, "\\sqrt{$1}")
    .replace(/\*\s*(?=\\sqrt|[a-zA-Z])/g, "");

  const slash = s.indexOf("/");
  if (slash > -1) {
    const num = s.slice(0, slash);
    const den = s.slice(slash + 1);
    return `\\dfrac{${num}}{${den}}`;
  }
  return s;
}



export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? (userAnswer.raw ?? userAnswer.value) : userAnswer;
  if (!raw || String(raw).trim() === "") return <i>(brak odpowiedzi)</i>;
  const pretty = formatRootFracLatex(raw);
  return (
    <div style={{ marginTop: "6px", marginBottom: "6px" }}>
      <InlineMath math={`\\displaystyle R = ${pretty}`} />
    </div>
  );
}


export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,  
};
